import React, { useEffect, useMemo, useState } from 'react';

const FOLDER_KEY = 'portfolioLab.selectedFolder';
const ALL = '__all__';

// Editable metadata as form strings, so a cleared cell round-trips as "" and is
// saved as NULL. Mirrors the registry/mapping editors: the grid IS the form.
const rowFromPortfolio = (p) => ({
  name: p.name || '',
  client: p.client || '',
  portfolio_code: p.portfolio_code || '',
  description: p.description || '',
  objective_type: p.objective_type || 'cpi',
  // return_objective is stored as a decimal (0.035) and edited as a percent (3.5)
  return_objective: p.return_objective != null ? String(+(p.return_objective * 100).toFixed(4)) : '',
  time_horizon: p.time_horizon != null ? String(p.time_horizon) : '',
});

const rowsEqual = (a, b) =>
  !!a && !!b && Object.keys(a).every((k) => a[k] === b[k]);

const SavedPortfolios = ({
  portfolios,
  loading,
  activeId,
  onLoad,
  onDelete,
  onDuplicate,
  onNew,
  onSaveMeta,
}) => {
  const [grid, setGrid] = useState({});   // id -> edited row
  const [base, setBase] = useState({});   // id -> row as last loaded from the server
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedFolder, setSelectedFolder] = useState(() => {
    try {
      return localStorage.getItem(FOLDER_KEY) || ALL;
    } catch {
      return ALL;
    }
  });
  const [search, setSearch] = useState('');

  // Re-sync from the server list, but keep cells the user has edited and not yet
  // saved (a duplicate/delete elsewhere on the page refreshes this prop).
  useEffect(() => {
    const nextBase = {};
    portfolios.forEach((p) => { nextBase[p.id] = rowFromPortfolio(p); });
    setBase(nextBase);
    setGrid((prev) => {
      const next = {};
      portfolios.forEach((p) => {
        const edited = prev[p.id];
        const wasDirty = edited && !rowsEqual(edited, base[p.id] || nextBase[p.id]);
        next[p.id] = wasDirty ? edited : nextBase[p.id];
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios]);

  const chooseFolder = (name) => {
    setSelectedFolder(name);
    try {
      localStorage.setItem(FOLDER_KEY, name);
    } catch {
      /* ignore storage errors */
    }
  };

  const setCell = (id, field, value) => {
    setGrid((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    setSuccess(null);
  };

  const dirtyIds = useMemo(
    () => portfolios.filter((p) => grid[p.id] && !rowsEqual(grid[p.id], base[p.id])).map((p) => p.id),
    [portfolios, grid, base],
  );

  // Client list drives both the folder filter and the name autocomplete.
  const clients = useMemo(() => {
    const set = new Set();
    portfolios.forEach((p) => {
      const c = (grid[p.id]?.client ?? p.client ?? '').trim();
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [portfolios, grid]);

  const counts = useMemo(() => {
    const map = {};
    portfolios.forEach((p) => {
      const c = (grid[p.id]?.client ?? p.client ?? '').trim() || 'Unassigned';
      map[c] = (map[c] || 0) + 1;
    });
    return map;
  }, [portfolios, grid]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = portfolios.filter((p) => {
      const r = grid[p.id] || rowFromPortfolio(p);
      const client = r.client.trim() || 'Unassigned';
      if (selectedFolder !== ALL && client !== selectedFolder) return false;
      if (q) {
        const hay = `${r.name} ${r.portfolio_code} ${r.client} ${r.description}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return rows.sort((a, b) => {
      const ra = grid[a.id] || rowFromPortfolio(a);
      const rb = grid[b.id] || rowFromPortfolio(b);
      const ca = ra.client.trim() || '￿';  // Unassigned sorts last
      const cb = rb.client.trim() || '￿';
      return ca.localeCompare(cb) || ra.name.localeCompare(rb.name);
    });
  }, [portfolios, grid, search, selectedFolder]);

  const handleSave = async () => {
    if (dirtyIds.length === 0) return;

    const updates = [];
    for (const id of dirtyIds) {
      const r = grid[id];
      const label = base[id]?.name || `#${id}`;
      if (!r.name.trim()) {
        setError(`“${label}” needs a portfolio name`);
        return;
      }
      const ret = r.return_objective !== '' ? parseFloat(r.return_objective) : null;
      if (ret != null && (Number.isNaN(ret) || ret < 0 || ret > 20)) {
        setError(`“${label}”: return objective must be between 0% and 20%`);
        return;
      }
      const horizon = r.time_horizon !== '' ? parseInt(r.time_horizon, 10) : null;
      if (horizon != null && (Number.isNaN(horizon) || horizon < 1 || horizon > 30)) {
        setError(`“${label}”: time horizon must be between 1 and 30 years`);
        return;
      }
      updates.push({
        id,
        fields: {
          name: r.name.trim(),
          client: r.client.trim() || null,
          portfolio_code: r.portfolio_code.trim() || null,
          description: r.description.trim() || null,
          objective_type: r.objective_type || 'cpi',
          return_objective: ret != null ? ret / 100 : null,
          time_horizon: horizon,
        },
      });
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await onSaveMeta(updates);
      setSuccess(`Saved ${updates.length} portfolio${updates.length === 1 ? '' : 's'}`);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setGrid(() => {
      const next = {};
      portfolios.forEach((p) => { next[p.id] = base[p.id] || rowFromPortfolio(p); });
      return next;
    });
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <div className="saved-portfolios">
        <div className="text-center py-2 text-muted" style={{ fontSize: '0.85rem' }}>
          Loading saved portfolios...
        </div>
      </div>
    );
  }

  return (
    <div className="saved-portfolios">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <h6 className="mb-0" style={{ color: 'var(--primary-color)', fontWeight: 600, fontSize: '0.9rem' }}>
          Saved Portfolios
        </h6>
        <span className="text-muted" style={{ fontSize: '0.78rem' }}>
          Edit details directly in the table, then save.
        </span>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible py-2" style={{ fontSize: '0.82rem' }}>
          {error}
          <button className="btn-close" onClick={() => setError(null)} />
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible py-2" style={{ fontSize: '0.82rem' }}>
          {success}
          <button className="btn-close" onClick={() => setSuccess(null)} />
        </div>
      )}

      {portfolios.length === 0 ? (
        <>
          <div className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
            No saved portfolios yet. Build a portfolio in the Build tab and save it.
          </div>
          <button
            className="btn btn-sm"
            style={{ background: 'var(--primary-color)', color: 'var(--white-color)', fontSize: '0.78rem' }}
            onClick={onNew}
          >
            + New Portfolio
          </button>
        </>
      ) : (
        <>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', minWidth: '180px', fontSize: '0.82rem', borderColor: 'var(--input-border)' }}
              value={selectedFolder}
              onChange={(e) => chooseFolder(e.target.value)}
              title="Filter by client"
            >
              <option value={ALL}>All clients ({portfolios.length})</option>
              {Object.keys(counts).sort((a, b) => {
                if (a === 'Unassigned') return 1;
                if (b === 'Unassigned') return -1;
                return a.localeCompare(b);
              }).map((c) => (
                <option key={c} value={c}>{c} ({counts[c]})</option>
              ))}
            </select>
            <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '320px' }}>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ fontSize: '0.82rem', borderColor: 'var(--input-border)' }}
                placeholder="Search name, code, client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setSearch('')}
                  style={{
                    position: 'absolute', right: '2px', top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'none', color: 'var(--gray-color)', padding: '0 0.4rem',
                    fontSize: '0.9rem', lineHeight: 1,
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            <span className="text-muted ms-auto" style={{ fontSize: '0.75rem' }}>
              showing {visible.length} of {portfolios.length}
            </span>
            {dirtyIds.length > 0 && (
              <button
                className="btn btn-outline-secondary btn-sm"
                style={{ fontSize: '0.78rem' }}
                onClick={handleRevert}
                disabled={saving}
              >
                Discard
              </button>
            )}
            <button
              className="btn btn-sm"
              style={{ background: 'var(--primary-color)', color: 'var(--white-color)', fontSize: '0.78rem' }}
              onClick={handleSave}
              disabled={saving || dirtyIds.length === 0}
            >
              {saving
                ? 'Saving...'
                : dirtyIds.length > 0
                  ? `Save ${dirtyIds.length} change${dirtyIds.length === 1 ? '' : 's'}`
                  : 'Save'}
            </button>
            <button
              className="btn btn-outline-primary btn-sm"
              style={{ fontSize: '0.78rem' }}
              onClick={onNew}
            >
              + New Portfolio
            </button>
          </div>

          <datalist id="saved-portfolio-clients">
            {clients.map((c) => <option key={c} value={c} />)}
          </datalist>

          <div className="table-responsive saved-portfolios-table" style={{ maxHeight: '64vh' }}>
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: 340 }}>Name</th>
                  <th style={{ minWidth: 150 }}>Client</th>
                  <th style={{ minWidth: 120 }}>Code</th>
                  <th style={{ minWidth: 180 }}>Description</th>
                  <th style={{ width: 110 }}>Objective</th>
                  <th style={{ width: 90 }} title="Return objective above CPI / cash, or absolute">Return %</th>
                  <th style={{ width: 90 }}>Horizon</th>
                  <th style={{ width: 70 }} className="text-center">Funds</th>
                  <th style={{ width: 90 }}>Updated</th>
                  <th style={{ width: 110 }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => {
                  const r = grid[p.id] || rowFromPortfolio(p);
                  const isDirty = dirtyIds.includes(p.id);
                  const isActive = activeId === p.id;
                  return (
                    <tr
                      key={p.id}
                      className={isActive ? 'row-active' : ''}
                      style={isDirty ? { background: '#fff8e6' } : undefined}
                    >
                      <td>
                        <input
                          className="form-control form-control-sm"
                          value={r.name}
                          title={r.name}
                          maxLength={200}
                          onChange={(e) => setCell(p.id, 'name', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          list="saved-portfolio-clients"
                          value={r.client}
                          maxLength={200}
                          placeholder="Unassigned"
                          onChange={(e) => setCell(p.id, 'client', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          style={{ fontFamily: 'monospace' }}
                          value={r.portfolio_code}
                          maxLength={50}
                          placeholder="—"
                          onChange={(e) => setCell(p.id, 'portfolio_code', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          value={r.description}
                          placeholder="—"
                          onChange={(e) => setCell(p.id, 'description', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={r.objective_type}
                          onChange={(e) => setCell(p.id, 'objective_type', e.target.value)}
                        >
                          <option value="cpi">CPI+</option>
                          <option value="cash">Cash+</option>
                          <option value="absolute">Absolute</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={r.return_objective}
                          min="0"
                          max="20"
                          step="0.25"
                          placeholder="—"
                          onChange={(e) => setCell(p.id, 'return_objective', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={r.time_horizon}
                          min="1"
                          max="30"
                          step="1"
                          placeholder="—"
                          onChange={(e) => setCell(p.id, 'time_horizon', e.target.value)}
                        />
                      </td>
                      <td className="text-center text-muted">{p.fund_count}</td>
                      <td className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {p.updated_at
                          ? new Date(p.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
                          : '—'}
                      </td>
                      <td className="text-end text-nowrap">
                        <button
                          className="btn btn-sm p-0 me-2 row-action"
                          onClick={() => onLoad(p)}
                          title={isActive ? 'Currently loaded — reload into the builder' : 'Load into the builder'}
                          style={{ color: 'var(--primary-color)' }}
                        >
                          <i className="bi bi-box-arrow-in-right" />
                        </button>
                        <button
                          className="btn btn-sm p-0 me-2 row-action"
                          onClick={() => onDuplicate(p.id)}
                          title="Duplicate"
                          style={{ color: 'var(--info-color)' }}
                        >
                          <i className="bi bi-files" />
                        </button>
                        <button
                          className="btn btn-sm p-0 row-action"
                          onClick={() => {
                            if (window.confirm(`Delete "${r.name}"?`)) onDelete(p.id);
                          }}
                          title="Delete"
                          style={{ color: '#dc3545' }}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>
                      No portfolios match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SavedPortfolios;
